import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Button, ProgressBar, Badge, Alert, Table } from 'react-bootstrap';
import { Activity, Users, Server, AlertTriangle, TrendingUp, RefreshCw, CheckCircle } from 'lucide-react';
import { getDashboardStats, getTokenUsageStats } from '../services/stats';
import { getReactionLogs } from '../services/logs';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useNavigate } from 'react-router-dom';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  variant = 'primary',
  loading = false 
}) => {
  const variantClasses = {
    primary: 'text-primary bg-primary-subtle',
    success: 'text-success bg-success-subtle',
    warning: 'text-warning bg-warning-subtle',
    danger: 'text-danger bg-danger-subtle',
  };

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <Card.Subtitle className="text-muted mb-2 fw-medium small text-uppercase">
              {title}
            </Card.Subtitle>
            {loading ? (
              <div className="placeholder-glow">
                <div className="placeholder col-6" style={{ height: '2.5rem' }}></div>
              </div>
            ) : (
              <Card.Title className="display-6 fw-bold mb-2 text-dark">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </Card.Title>
            )}
            {trend && !loading && (
              <div className="d-flex align-items-center text-success small fw-medium">
                <TrendingUp size={14} className="me-1" />
                {trend}
              </div>
            )}
          </div>
          <div className={`p-3 rounded-3 ${variantClasses[variant]}`}>
            <Icon size={28} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const HealthCard = ({ 
  title, 
  value, 
  description,
  variant = 'primary' 
}) => {
  const getVariant = (val) => {
    if (val > 75) return 'success';
    if (val > 50) return 'warning';
    return 'danger';
  };

  const progressVariant = getVariant(value);

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Card.Title className="h6 mb-0 fw-semibold">{title}</Card.Title>
          <Badge bg={progressVariant} className="fs-6 px-2 py-1">
            {Math.round(value)}%
          </Badge>
        </div>
        <ProgressBar 
          variant={progressVariant} 
          now={value} 
          className="mb-2"
          style={{ height: '10px' }}
        />
        {description && (
          <small className="text-muted fw-medium">{description}</small>
        )}
      </Card.Body>
    </Card>
  );
};

const ActivityChart = ({ data, loading }) => {
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Requests Over Time',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
        fill: true,
      },
    },
  };

  const chartData = {
    labels: data?.labels || [],
    datasets: [
      {
        label: 'Total Requests',
        data: data?.totalRequests || [],
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        fill: true,
      },
      {
        label: 'Successful Requests',
        data: data?.successfulRequests || [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        fill: true,
      },
    ],
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted mt-2">Loading chart data...</p>
      </div>
    );
  }

  return (
    <div style={{ height: '300px' }}>
      <Line options={chartOptions} data={chartData} />
    </div>
  );
};

const RecentLogs = ({ limit = 5 }) => {
  const navigate = useNavigate(); 
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRecentLogs = useCallback(async () => {
    try {
      setLoading(true);
      const logsData = await getReactionLogs({ limit, order: 'desc' });
      setLogs(logsData);
    } catch (error) {
      console.error('Error fetching recent logs:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchRecentLogs();
  }, [fetchRecentLogs]);

  const getVariant = (success) => {
    return success ? 'success' : 'danger';
  };

  const getIcon = (success) => {
    return success ? '✅' : '❌';
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const logTime = new Date(timestamp);
    const diffMs = now - logTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {logs.slice(0, limit).map((log) => (
        <div key={log.id} className="d-flex align-items-start py-3 border-bottom border-light">
          <div className="me-3 mt-1">
            <span style={{ fontSize: '1.1rem' }}>{getIcon(log.success)}</span>
          </div>
          <div className="flex-grow-1">
            <div className="fw-semibold text-dark mb-1">
              {log.success ? 'Reaction processed successfully' : 'Failed to process reaction'}
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">{formatTime(log.created_at)}</small>
              <Badge bg={getVariant(log.success)} className="small">
                {log.success ? 'Success' : 'Error'}
              </Badge>
            </div>
          </div>
        </div>
      ))}
      <div className="text-center pt-3">
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={() => navigate('/logs')}
        >
          View All Logs
        </Button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboardStats = await getDashboardStats();
      setStats(dashboardStats);
    } catch (e) {
      console.error('Error fetching stats:', e);
      setError('Failed to load dashboard stats. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChartData = useCallback(async () => {
    setChartLoading(true);
    try {
      // Fetch reaction logs for the last 7 days to build the chart
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      
      // Format dates for API call
      const formatDate = (date) => date.toISOString().split('T')[0];
      
      // Get logs for the last 7 days
      const logs = await getReactionLogs({
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        limit: 1000 // Increase limit to get more data
      });
      
      // Group logs by date
      const logsByDate = {};
      const currentDate = new Date(startDate);
      
      // Initialize with empty data for all days
      while (currentDate <= endDate) {
        const dateKey = currentDate.toISOString().split('T')[0];
        logsByDate[dateKey] = {
          total: 0,
          successful: 0
        };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Count logs by date
      logs.forEach(log => {
        const logDate = new Date(log.created_at).toISOString().split('T')[0];
        if (logsByDate[logDate]) {
          logsByDate[logDate].total += 1;
          if (log.success) {
            logsByDate[logDate].successful += 1;
          }
        }
      });
      
      // Prepare data for the chart
      const labels = [];
      const totalRequests = [];
      const successfulRequests = [];
      
      Object.keys(logsByDate)
        .sort()
        .forEach(date => {
          const formattedDate = new Date(date).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          });
          
          labels.push(formattedDate);
          totalRequests.push(logsByDate[date].total);
          successfulRequests.push(logsByDate[date].successful);
        });
      
      setChartData({
        labels,
        totalRequests,
        successfulRequests
      });
    } catch (e) {
      console.error('Error fetching chart data:', e);
      setError('Failed to load chart data. Please try again.');
    } finally {
      setChartLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchChartData();
    
    // Set up polling for real-time updates (every 30 seconds)
    const statsIntervalId = setInterval(fetchStats, 30000);
    const chartIntervalId = setInterval(fetchChartData, 60000);
    
    return () => {
      clearInterval(statsIntervalId);
      clearInterval(chartIntervalId);
    };
  }, [fetchStats, fetchChartData]);

  // Calculate success rate percentage
  const successRate = stats 
    ? stats.total_requests > 0 
      ? (stats.successful_requests / stats.total_requests) * 100 
      : 0 
    : 0;

  return (
    <div>
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-2 text-dark">Dashboard Overview</h1>
          <p className="text-muted mb-0 lead">
            Monitor your system performance and activity in real-time
          </p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => {
            fetchStats();
            fetchChartData();
          }} 
          disabled={loading || chartLoading}
          className="d-flex align-items-center shadow-sm px-4"
        >
          <RefreshCw size={16} className={`me-2 ${loading || chartLoading ? 'spin' : ''}`} />
          {loading || chartLoading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4 border-0 shadow-sm">
          <AlertTriangle size={20} className="me-2" />
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Requests"
            value={stats?.total_requests || 0}
            icon={Activity}
            trend="+12% from last week"
            variant="primary"
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Active Proxies"
            value={stats?.active_proxies || 0}
            icon={Server}
            trend="+2 new proxies"
            variant="success"
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Success Rate"
            value={stats ? `${successRate.toFixed(1)}%` : '0%'}
            icon={CheckCircle}
            trend="Excellent performance"
            variant="success"
            loading={loading}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Active Tokens"
            value={stats?.active_tokens || 0}
            icon={Users}
            trend="+5% this month"
            variant="warning"
            loading={loading}
          />
        </Col>
      </Row>

      {/* Charts and Health */}
      <Row className="g-4 mb-4">
        <Col xs={12} xl={8}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-white border-0 py-4">
              <Card.Title className="h5 mb-0 fw-bold text-dark">Activity Overview</Card.Title>
              <small className="text-muted">Real-time system monitoring</small>
            </Card.Header>
            <Card.Body>
              <ActivityChart data={chartData} loading={chartLoading} />
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} xl={4}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-white border-0 py-4">
              <Card.Title className="h5 mb-0 fw-bold text-dark">System Health</Card.Title>
              <small className="text-muted">Current system status</small>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <HealthCard
                    title="Token Health"
                    value={stats?.token_health || 0}
                    description="Authentication system status"
                  />
                </Col>
                <Col xs={12}>
                  <HealthCard
                    title="Proxy Health"
                    value={stats?.proxy_health || 0}
                    description="Proxy network performance"
                  />
                </Col>
              </Row>
              {stats?.last_updated && (
                <div className="mt-4 pt-3 border-top">
                  <small className="text-muted fw-medium">
                    Last updated: {new Date(stats.last_updated).toLocaleString()}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-0 py-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Card.Title className="h5 mb-0 fw-bold text-dark">Recent Activity</Card.Title>
              <small className="text-muted">Latest system events and notifications</small>
            </div>
            <Badge bg="primary" className="px-3 py-2">Live</Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <RecentLogs limit={5} />
        </Card.Body>
      </Card>
    </div>
  );
};

export default Dashboard;